import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Toaster, ToastBar } from "react-hot-toast";
import toast from "react-hot-toast";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { useMediaQuery } from "@/hooks/useMediaQuery";
import { AppProvider, useApp } from "@/contexts/AppContext";
import { StaffNotificationProvider } from "@/contexts/StaffNotificationContext";

import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import CustomerView from "./pages/CustomerView";
import CustomerScan from "./pages/CustomerScan";

const queryClient = new QueryClient();

// Inner component that has access to the AppContext
const AppRoutes = () => {
  const { currentUser } = useApp();
  const isTablet = useMediaQuery('(max-width: 1024px)');

  // Conditionally wrap with StaffNotificationProvider only when user is logged in
  const renderRoutes = () => {
    const routeContent = (
      <>
        <Toaster
          position={isTablet ? "top-center" : "bottom-right"}
          toastOptions={{
            duration: 4000,
            style: {
              background: '#FFFFFF',
              color: '#1e293b',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              borderRadius: '8px',
              padding: '12px 16px',
            },
          }}
        >
          {(t) => (
            <ToastBar toast={t}>
              {({ icon, message }) => (
                <>
                  {icon}
                  <div style={{ flexGrow: 1 }}>{message}</div>
                  {t.type !== 'loading' && (
                    <button 
                      onClick={() => toast.dismiss(t.id)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#1e293b',
                        opacity: 0.5,
                        cursor: 'pointer',
                        padding: '0 5px',
                        fontSize: '14px'
                      }}
                    >
                      ✕
                    </button>
                  )}
                </>
              )}
            </ToastBar>
          )}
        </Toaster>
        <Routes>
          {/* Redirect from root to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          {/* Login page (former Index) */}
          <Route path="/login" element={<Index />} />
          {/* Redirect from old scan path to new scanqr path */}
          <Route path="/scan" element={<Navigate to="/scanqr" replace />} />
          <Route path="/scanqr" element={<CustomerScan />} />
          <Route path="/customer/:id" element={<CustomerView />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </>
    );

    // Only wrap with StaffNotificationProvider if user is logged in
    return currentUser ? (
      <StaffNotificationProvider>
        {routeContent}
      </StaffNotificationProvider>
    ) : routeContent;
  };

  return renderRoutes();
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AppProvider>
          <Router>
            <AppRoutes />
          </Router>
        </AppProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
